// Layout primitives for the create slide-over. Mirrors Drillcut's
// `primitives.tsx` but every colour reads from BSP design tokens
// (--bsp-* variables) instead of Drillcut purple.
//
// `FormSection`, `FormLabel`, `Hint`, `twoCol`, `iconBtn`,
// `inlineInput`, `ghostAdd` keep the visual cadence of the original
// (DM Sans labels, condensed body), translated to Barlow.

import type { CSSProperties, ReactNode } from "react";

// Design-system token façade. Reads from globals.css variables so dark
// mode and future theme swaps come along for free.
export const DC = {
  primary: "var(--bsp-blue)",
  primaryHover: "var(--bsp-blue-deep)",
  primaryTint: "var(--bsp-blue-tint)",
  primaryPale: "var(--bsp-blue-pale)",
  primaryBorder: "var(--bsp-blue-border)",
  grey: "var(--bsp-fg-muted)",
  greyDeep: "var(--ink-700)",
  greyMid: "var(--ink-400)",
  border: "var(--bsp-border)",
  borderStrong: "var(--bsp-border-strong)",
  surface: "var(--bsp-surface)",
  surfaceMuted: "var(--bsp-surface-muted)",
  dark: "var(--bsp-dark)",
  green: "var(--bsp-green)",
  greenFg: "var(--bsp-green-fg)",
  greenTint: "var(--bsp-green-tint)",
  orange: "var(--bsp-orange)",
  orangeFg: "var(--bsp-orange-fg)",
  orangeTint: "var(--bsp-orange-tint)",
  red: "var(--bsp-red)",
  redFg: "var(--bsp-red-fg)",
  redTint: "var(--bsp-red-tint)",
};

// Entity colours — used for the 3px left rail on inline milestone /
// subtask rows and on the slide-over's 4px accent strip across the
// top edge. BSP keeps everything within the blue / green / orange /
// grey palette; Drillcut's purple goes to BSP blue, the Drillcut
// teal milestone stays green, etc.
export const ENTITY = {
  rock:        { color: "var(--bsp-blue)",   tint: "var(--bsp-blue-tint)" },
  milestone:   { color: "var(--bsp-green)",  tint: "var(--bsp-green-tint)" },
  todo:        { color: "var(--bsp-blue-2)", tint: "var(--bsp-blue-pale)" },
  subtask:     { color: "var(--ink-500)",    tint: "var(--ink-100)" },
  opportunity: { color: "var(--bsp-orange)", tint: "var(--bsp-orange-tint)" },
} as const;

export function FormSection({
  children,
  style,
}: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", marginBottom: 18, ...style }}>
      {children}
    </div>
  );
}

export function FormLabel({
  children,
  required,
  hint,
}: { children: ReactNode; required?: boolean; hint?: ReactNode }) {
  return (
    <label
      style={{
        font: "700 11px/1 var(--bsp-font-sans)",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: DC.grey,
        marginBottom: 8,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>{children}</span>
      {required ? (
        <span style={{ color: DC.orange, fontSize: 14 }} aria-hidden>•</span>
      ) : null}
      {hint ? (
        <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none", color: DC.grey, fontSize: 11 }}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function Hint({
  children,
  style,
}: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: DC.grey,
        lineHeight: 1.4,
        marginTop: 6,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Two-column grid used for paired fields (owner+team, quarter+status,
// rock+milestone, due+priority). Falls back to single column under
// 420px so the slide-over still works on narrow viewports.
export const twoCol: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
};

export const inlineInput: CSSProperties = {
  width: "100%",
  height: 28,
  padding: "0 8px",
  border: 0,
  background: "transparent",
  outline: 0,
  font: "400 13px/1 var(--bsp-font-sans)",
  color: "var(--bsp-fg)",
};

export const iconBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  border: 0,
  background: "transparent",
  color: DC.grey,
  borderRadius: 4,
  cursor: "pointer",
};

export const ghostAdd: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  height: 32,
  padding: "0 12px",
  border: `1px dashed ${DC.borderStrong}`,
  background: "transparent",
  borderRadius: 6,
  color: DC.grey,
  font: "600 12px/1 var(--bsp-font-sans)",
  cursor: "pointer",
};
